import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Deliberately silent about existence — always show the success card.
      await api.post("/auth/forgot-password", { email });
    } catch (_) { /* still show success — no user enumeration */ }
    finally {
      setSent(true);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] grid place-items-center px-4 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {!sent ? (
          <>
            <div className="mb-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-100 dark:bg-sky-950/40 grid place-items-center">
                <Mail className="w-5 h-5 text-sky-700 dark:text-sky-300"/>
              </div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100">Forgot password?</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Enter your email and we'll send a reset link.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                  />
                </div>
              </div>
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-sm font-medium disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
                Send reset link
                {!busy && <ArrowRight className="w-4 h-4"/>}
              </button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
              <Link to="/login" className="text-slate-700 dark:text-slate-300 hover:underline" data-testid="forgot-back-to-signin">← Back to sign in</Link>
            </div>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-sent">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center">
              <Mail className="w-6 h-6 text-emerald-700 dark:text-emerald-300"/>
            </div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Check your inbox</h1>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
              If <span className="font-medium text-slate-900 dark:text-slate-100">{email}</span> matches an account, we've sent a password reset link. The link is valid for one hour.
            </p>
            <Link to="/login" className="inline-block mt-6 text-sm text-sky-700 dark:text-sky-300 hover:underline">
              ← Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
